import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { DragDropContext, type DropResult } from '@hello-pangea/dnd';
import type {
  AgentApiJobChangeProcessBoardsItem,
  AgentApiJobChangeProcessBoardsShow,
  AgentApiJobChangeProcessesRecord,
} from '@/types/serializers';
import type { Color } from '@/features/agent/candidates/types/CandidateBoard';
import { MdAdd } from 'react-icons/md';
import { Button } from '@/components/Button';
import JobChangeProcessBoardColumn from './JobChangeProcessBoardColumn';
import type { BoardPageState } from '../../api/useFetchJobChangeProcessBoardsDetails';
import usePostJobChangeProcessBoardsMove from '../../api/usePostJobChangeProcessBoardsMove';
import useUpdateJobChangeProcessBoard from '../../api/useUpdateJobChangeProcessBoard';
import useDeleteJobChangeProcessBoard from '../../api/useDeleteJobChangeProcessBoard';
import { showErrorToast, showSuccessToast } from '../../lib/toast';
import { confirm } from '@/utils/confirm';

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

type Props = {
  boards: AgentApiJobChangeProcessBoardsItem[];
  boardsData: (AgentApiJobChangeProcessBoardsShow | undefined)[];
  boardStates: Record<number, BoardPageState>;
  uncategorized?: AgentApiJobChangeProcessBoardsShow;
  boardsRefetch: (options?: { resetPages?: boolean }) => Promise<unknown>;
  reloadBoards: (ids: number[]) => void;
  uncategorizedRefetch: (options?: { nextPage?: boolean; resetPage?: boolean }) => Promise<unknown>;
  onSelect: (jobChangeProcess: AgentApiJobChangeProcessesRecord) => void;
  selectedId?: number;
  onCreateBoard: () => Promise<void>;
  isCreatingBoard?: boolean;
  onLoadMoreBoard: (boardId: number) => void;
  uncategorizedHasNextPage?: boolean;
  uncategorizedIsFetching?: boolean;
  isLoading?: boolean;
  filterKey?: string;
};

export type BoardScreenHandle = {
  scrollToEnd: () => void;
};

const JobChangeProcessBoardScreen = forwardRef<BoardScreenHandle, Props>(function BoardScreen(
  {
    boards,
    boardsData,
    boardStates,
    uncategorized,
    boardsRefetch,
    reloadBoards,
    uncategorizedRefetch,
    onSelect,
    selectedId,
    onCreateBoard,
    isCreatingBoard = false,
    onLoadMoreBoard,
    uncategorizedHasNextPage = false,
    uncategorizedIsFetching = false,
    isLoading = false,
    filterKey = '',
  },
  ref
) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { mutateAsync: moveJobChangeProcess } = usePostJobChangeProcessBoardsMove();
  const { mutateAsync: updateBoard } = useUpdateJobChangeProcessBoard();
  const { mutateAsync: deleteBoard } = useDeleteJobChangeProcessBoard();
  const [grouped, setGrouped] = useState<Record<string, AgentApiJobChangeProcessesRecord[]>>({});
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [isLoadingMoreUncategorized, setIsLoadingMoreUncategorized] = useState(false);
  const prevFilterKeyRef = useRef(filterKey);
  const prevSourcesRef = useRef<Record<string, object>>({});

  useImperativeHandle(ref, () => ({
    scrollToEnd: () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          left: scrollContainerRef.current.scrollWidth,
          behavior: 'smooth',
        });
      }
    },
  }));

  useEffect(() => {
    const sources: Record<string, AgentApiJobChangeProcessBoardsShow> = {};
    if (uncategorized) sources.uncategorized = uncategorized;
    boardsData.forEach((data) => {
      if (data?.board) sources[`board_${data.board.id}`] = data;
    });

    const filterChanged = prevFilterKeyRef.current !== filterKey;
    prevFilterKeyRef.current = filterKey;
    // 変わった列だけ反映する。全列を上書きすると D&D の楽観更新が古いデータで戻る
    const changedKeys = Object.keys(sources).filter(
      (key) => filterChanged || prevSourcesRef.current[key] !== sources[key]
    );
    prevSourcesRef.current = sources;

    const nextGrouped = Object.fromEntries(changedKeys.map((key) => [key, sources[key].records]));
    const nextCounts = Object.fromEntries(
      changedKeys.map((key) => [key, sources[key].total_count])
    );
    setGrouped((prev) => (filterChanged ? nextGrouped : { ...prev, ...nextGrouped }));
    setCounts((prev) => (filterChanged ? nextCounts : { ...prev, ...nextCounts }));
  }, [boardsData, uncategorized, filterKey]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const destinationDroppableId = result.destination.droppableId;
    const sourceDroppableId = result.source.droppableId;
    if (destinationDroppableId === 'uncategorized') return;

    const processId = Number(result.draggableId);
    const moved = (grouped[sourceDroppableId] || []).find((record) => record.id === processId);
    if (!moved) return;

    const previousGrouped = grouped;
    const previousCounts = counts;

    const nextGrouped = { ...grouped };
    const sourceRecords = [...(nextGrouped[sourceDroppableId] || [])];
    const removedIndex = sourceRecords.findIndex((record) => record.id === processId);
    if (removedIndex === -1) return;
    sourceRecords.splice(removedIndex, 1);
    nextGrouped[sourceDroppableId] = sourceRecords;

    const destinationRecords =
      sourceDroppableId === destinationDroppableId
        ? sourceRecords
        : [...(nextGrouped[destinationDroppableId] || [])];
    destinationRecords.splice(result.destination.index, 0, moved);
    nextGrouped[destinationDroppableId] = destinationRecords;
    setGrouped(nextGrouped);

    if (sourceDroppableId !== destinationDroppableId) {
      setCounts((prev) => ({
        ...prev,
        [sourceDroppableId]: Math.max((prev[sourceDroppableId] || 0) - 1, 0),
        [destinationDroppableId]: (prev[destinationDroppableId] || 0) + 1,
      }));
    }

    try {
      await moveJobChangeProcess({
        job_change_process_id: processId,
        target_board_id: Number(destinationDroppableId.replace('board_', '')),
        order_number: result.destination.index,
      });
      // 蓄積済みのページは移動前のままなので、移動に関わった列は取り直す
      reloadBoards(
        [...new Set([sourceDroppableId, destinationDroppableId])]
          .filter((droppableId) => droppableId.startsWith('board_'))
          .map((droppableId) => Number(droppableId.replace('board_', '')))
      );
      if (sourceDroppableId === 'uncategorized') {
        uncategorizedRefetch({ resetPage: true });
      }
    } catch (error) {
      setGrouped(previousGrouped);
      setCounts(previousCounts);
      showErrorToast(errorMessage(error, 'カードの移動に失敗しました'));
    }
  };

  const handleTitleChange = async (boardId: number, title: string) => {
    try {
      await updateBoard({ id: boardId, title });
      await boardsRefetch();
    } catch (error) {
      showErrorToast(errorMessage(error, 'ボード名の変更に失敗しました'));
      await boardsRefetch();
    }
  };

  const handleColorChange = async (boardId: number, colorType: Color) => {
    try {
      await updateBoard({ id: boardId, color_type: colorType });
      await boardsRefetch();
    } catch (error) {
      showErrorToast(errorMessage(error, 'ボードの色の変更に失敗しました'));
    }
  };

  const handleDelete = async (boardId: number) => {
    const board = boards.find((record) => record.id === boardId);
    const cardCount = counts[`board_${boardId}`] ?? 0;
    const confirmed = await confirm({
      title: 'ボードを削除',
      message: cardCount
        ? `「${board?.title ?? ''}」を削除しますか？このボードの${cardCount}件は未分類に移動します。`
        : `「${board?.title ?? ''}」を削除しますか？`,
    });
    if (!confirmed) return;

    try {
      await deleteBoard({ targetBoardId: boardId });
      await boardsRefetch();
      await uncategorizedRefetch({ resetPage: true });
      showSuccessToast('ボードを削除しました');
    } catch (error) {
      showErrorToast(errorMessage(error, 'ボードの削除に失敗しました'));
    }
  };

  const handleLoadMoreUncategorized = async () => {
    if (isLoadingMoreUncategorized || !uncategorizedHasNextPage) return;

    setIsLoadingMoreUncategorized(true);
    try {
      await uncategorizedRefetch({ nextPage: true });
    } finally {
      setIsLoadingMoreUncategorized(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[200px] items-center justify-center">
        <div className="size-icon-md animate-spin rounded-full border-2 border-solid border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div ref={scrollContainerRef} className="hidden-scrollbar flex gap-sm overflow-x-auto px-lg">
        <JobChangeProcessBoardColumn
          droppableId="uncategorized"
          title="未分類"
          colorType="gray"
          jobChangeProcesses={grouped.uncategorized || []}
          selectedId={selectedId}
          onSelect={onSelect}
          totalCount={counts.uncategorized ?? 0}
          onLoadMore={handleLoadMoreUncategorized}
          hasNextPage={uncategorizedHasNextPage}
          isLoadingMore={isLoadingMoreUncategorized || uncategorizedIsFetching}
        />
        {boards.map((board) => (
          <JobChangeProcessBoardColumn
            key={board.id}
            droppableId={`board_${board.id}`}
            boardId={board.id}
            title={board.title}
            colorType={board.color_type as Color}
            jobChangeProcesses={grouped[`board_${board.id}`] || []}
            selectedId={selectedId}
            onSelect={onSelect}
            onTitleChange={(title) => handleTitleChange(board.id, title)}
            onColorChange={(color) => handleColorChange(board.id, color)}
            onDelete={handleDelete}
            totalCount={counts[`board_${board.id}`] ?? 0}
            onLoadMore={() => onLoadMoreBoard(board.id)}
            hasNextPage={boardStates[board.id]?.hasNextPage ?? false}
            isLoadingMore={boardStates[board.id]?.isFetching ?? false}
          />
        ))}
        <Button
          type="button"
          color="secondary"
          size="lg"
          className="w-[272px] min-w-[272px] justify-start self-start"
          onClick={onCreateBoard}
          disabled={isCreatingBoard}
        >
          <MdAdd className="size-icon-sm" />
          新規ボードを作成
        </Button>
      </div>
    </DragDropContext>
  );
});

export default JobChangeProcessBoardScreen;
